import React, { useEffect, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";
import { toast } from "../utils/toast";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

export default function CompanyRequirements() {
  const [resumes, setResumes] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadData = async () => {
    try {
      const response = await fetch(`${ApiURL}crm/company/get_resume_requirements.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load resume tags");
      }

      const resumeItems = result?.data?.resumes || [];
      const subCompanyItems = result?.data?.sub_companies || [];
      setResumes(resumeItems);
      setSubCompanies(subCompanyItems);
      setDrafts(
        Object.fromEntries(
          resumeItems.map((item) => [
            item.resume_id,
            {
              sub_company_ids: (item.sub_company_ids || []).map(String),
              requirement_notes: item.requirement_notes || "",
            },
          ])
        )
      );
    } catch (error) {
      console.error("Error loading requirements:", error);
      toast.error(error.message || "Failed to load requirements");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFieldChange = (resumeId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [resumeId]: {
        ...(prev[resumeId] || { sub_company_ids: [], requirement_notes: "" }),
        [field]: value,
      },
    }));
  };

  const handleSave = async (resumeId) => {
    const draft = drafts[resumeId] || { sub_company_ids: [], requirement_notes: "" };
    setSavingId(resumeId);
    try {
      const response = await fetch(`${ApiURL}crm/company/save_resume_requirement.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          resume_id: resumeId,
          sub_company_ids: draft.sub_company_ids || [],
          requirement_notes: draft.requirement_notes || "",
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to save requirement");
      }

      toast.success("Resume tag saved successfully.");
      await loadData();
    } catch (error) {
      console.error("Error saving requirement:", error);
      toast.error(error.message || "Failed to save requirement");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <CompanyLayout
      title="Requirements"
      description="Tag each resume to a sub company like Wipro so recruiters know which company the resume belongs to."
    >
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Resume</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Candidate</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Tagged Companies</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Requirement Notes</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {resumes.map((resume) => (
                <tr key={resume.resume_id} className="hover:bg-gray-50 align-top">
                  <td className="px-3 py-2 text-slate-900">
                    <div className="font-medium">{resume.original_filename}</div>
                    <div className="text-[10px] text-slate-500">
                      {resume.upload_date ? new Date(resume.upload_date).toLocaleDateString() : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    <div>{resume.candidate_name || "-"}</div>
                    <div className="text-[10px] text-slate-500">{resume.candidate_role || ""}</div>
                  </td>
                  <td className="px-3 py-2 min-w-[240px]">
                    <select
                      multiple
                      value={drafts[resume.resume_id]?.sub_company_ids || []}
                      onChange={(event) =>
                        handleFieldChange(
                          resume.resume_id,
                          "sub_company_ids",
                          Array.from(event.target.selectedOptions, (option) => option.value)
                        )
                      }
                      className="h-28 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs"
                    >
                      {subCompanies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <div className="mt-1 text-[10px] text-slate-400">
                      Current: {resume.sub_company_names?.join(", ") || "No tags"}
                    </div>
                  </td>
                  <td className="px-3 py-2 min-w-[220px]">
                    <textarea
                      rows={3}
                      value={drafts[resume.resume_id]?.requirement_notes || ""}
                      onChange={(event) => handleFieldChange(resume.resume_id, "requirement_notes", event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs"
                      placeholder="Optional notes for recruiter context"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleSave(resume.resume_id)}
                      disabled={savingId === resume.resume_id}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {savingId === resume.resume_id ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              ))}
              {resumes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                    No resumes available for tagging yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </CompanyLayout>
  );
}
